import React, { FunctionComponent, useEffect, useState } from 'react'
import { format } from 'date-fns'
import ReactJson from 'react-json-view'
import { Spinner } from 'reactstrap'

import axios from 'lib/axios'
import { ContentSectionCard } from 'components/TailwindUIToolkit'
import * as SC from 'components/TailwindUIToolkit/StyledComponents'

type CloseActivityEvent = {
  action?: string
  date_created?: string
  id?: string
  object_type?: string
  [key: string]: any
}

type Props = {
  userId: string
}

const formatActivityDate = (dateString?: string) => {
  if (!dateString) {
    return 'Unknown date'
  }

  const date = new Date(dateString)
  if (Number.isNaN(date.getTime())) {
    return 'Unknown date'
  }

  return format(date, 'MM/dd/yyyy @ hh:mm a')
}

const CloseActivityBody: FunctionComponent<React.PropsWithChildren<Props>> = ({ userId }) => {
  const [activities, setActivities] = useState<CloseActivityEvent[]>()
  const [error, setError] = useState('')

  useEffect(() => {
    let isMounted = true

    setActivities(undefined)
    setError('')

    axios
      .get(`/admin/users/${userId}/close_activity`)
      .then((response) => {
        if (isMounted) {
          setActivities(response.data ?? [])
        }
      })
      .catch((err) => {
        if (!isMounted) {
          return
        }

        if (err?.response?.status === 404) {
          setActivities([])
          return
        }

        setError(err?.response?.data?.message ?? err?.message ?? 'Unable to load Close activity')
      })

    return () => {
      isMounted = false
    }
  }, [userId])

  if (error) {
    return (
      <div className='p-6'>
        <SC.ErrorMessage>{error}</SC.ErrorMessage>
      </div>
    )
  }

  if (!activities) {
    return (
      <div className='p-6'>
        <Spinner />
      </div>
    )
  }

  if (!activities.length) {
    return <div className='p-6 text-sm text-gray-500'>No Close activity found for user</div>
  }

  return (
    <div className='space-y-4 p-6'>
      {activities.map((activity, index) => (
        <div key={activity.id ?? index}>
          <div className='text-sm font-semibold text-gray-900'>
            {activity.object_type ?? 'Activity'} {activity.action ?? ''}
            <span className='ml-2 font-normal text-gray-500'>
              {formatActivityDate(activity.date_created)}
            </span>
          </div>
          <div className='mt-2 rounded-md border border-gray-200 bg-gray-50 p-3'>
            <ReactJson
              collapsed
              displayDataTypes={false}
              enableClipboard={false}
              name={false}
              src={activity}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

const CloseActivity: FunctionComponent<React.PropsWithChildren<Props>> = ({ userId }) => (
  <ContentSectionCard title='Close Activity' size='large' isOpenDefault={false}>
    <CloseActivityBody userId={userId} />
  </ContentSectionCard>
)

export default CloseActivity
